import type { RouteObject } from "react-router";
import { Outlet } from "react-router";
import type { ReactNode } from "react";
import MainLayout from "@/layouts/MainLayout";
import AuthProvider from "@/provider/AuthProvider";
import AuthCallback from "@/provider/AuthProvider/AuthCallback";
import FullScreenLoading from "@/components/FullScreenLoading";
import i18n from "@/config/i18n";
import AskNovaChatbot from "@/components/app/AskNova/AskNovaChatbot";
import DebugWindow from "@/components/app/DebugWindow";
import { AccountProvider } from "@/context/account";
import { CustomerRouteProvider } from "@/context/customer";
import {
  FeatureFlaggedComponent,
  FeatureFlagProvider,
  useFeatureFlags,
} from "@/context/FeatureFlags";
import { UiFeatureFlagNames } from "@/gql/graphql";
import PageTitle from "../components/PageTitle";
import { APP_PATHS, ROUTE_PARAMS } from "@/constants";
import NavigateToAccessDenied from "@/components/app/NavigateToAccessDenied";
import Route404 from "@/components/app/Route404";
import { ErrorBoundary } from "@/layouts/ErrorBoundary";
import {
  DenyDealerWithoutRole,
  PermissionAction,
  PermissionCategory,
  PermissionGate,
} from "@/permissions";
import { MeProvider } from "@/context/me";
import { DeviceSyncWarmUp } from "@/context/DeviceSyncWarmUp";
import { AppDialogsProvider } from "@/context/dialogs";
import { useMachineTranslationPreviewSync } from "@/hooks/useMachineTranslationPreviewSync";
import { AuthenticatedShell } from "./shells/authenticatedShell.tsx";
import { AppEntryShell } from "./shells/appEntryShell.tsx";
import { SuperAdminShell } from "./shells/superAdminShell.tsx";
import ProtectedRoute from "@/provider/AuthProvider/ProtectedRoute";
import { FileDownloadRedirect } from "@/pages/FileDownload/FileDownloadRedirect";
import { NewRelicRoute } from "@/config/newrelic/NewRelicRoute";
import { ForbiddenApiToastRegistry } from "@/components/app/ForbiddenApiToastRegistry";
import { SupportSessionTimeoutRegistry } from "@/components/app/SupportSessionTimeoutRegistry";
import { modusSnackbarConfig, muiSnackbarConfig } from "@/config/snackbar";
import { NotificationsProvider } from "@toolpad/core";
import { DealerOwnerRoleGate } from "./dealerOwnerRoleGate";

const permissionOutlet = (
  resource: PermissionCategory,
  action: PermissionAction
) => (
  <PermissionGate
    resource={resource}
    action={action}
    fallback={<NavigateToAccessDenied />}
  >
    <Outlet />
  </PermissionGate>
);

async function lazyDefault<T>(
  importer: () => Promise<{ default: T }>
): Promise<T> {
  const mod = await importer();
  return mod.default;
}

const loadChangeShipToContactRoute = () =>
  lazyDefault(() => import("@/pages/Customers/ChangeShipToContact"));

const lazyRouteFallback = <FullScreenLoading />;

function MachineTranslationPreviewSync() {
  useMachineTranslationPreviewSync();
  return null;
}

const FeatureFlagNotifications = ({ children }: { children: ReactNode }) => {
  const { canUseFeatureFlag } = useFeatureFlags();
  const snackbarConfig = canUseFeatureFlag(UiFeatureFlagNames.UxUpdate)
    ? modusSnackbarConfig
    : muiSnackbarConfig;

  return (
    <NotificationsProvider
      slots={{ snackbar: snackbarConfig.slot }}
      slotProps={{ snackbar: snackbarConfig.slotProps }}
    >
      {children}
    </NotificationsProvider>
  );
};

const ProtectedRoot = () => {
  return (
    <AuthenticatedShell>
      <DeviceSyncWarmUp />
      <AccountProvider>
        <MeProvider>
          <FeatureFlagProvider>
            <FeatureFlagNotifications>
              <ForbiddenApiToastRegistry />
              <SupportSessionTimeoutRegistry />
              <MachineTranslationPreviewSync />
              <AppDialogsProvider>
                <FeatureFlaggedComponent
                  flagName={UiFeatureFlagNames.DebugMenu}
                >
                  <DebugWindow />
                </FeatureFlaggedComponent>

                <FeatureFlaggedComponent
                  flagName={UiFeatureFlagNames.CustomerAssistant}
                >
                  <AskNovaChatbot />
                </FeatureFlaggedComponent>

                <MainLayout />
              </AppDialogsProvider>
            </FeatureFlagNotifications>
          </FeatureFlagProvider>
        </MeProvider>
      </AccountProvider>
    </AuthenticatedShell>
  );
};

const routes: RouteObject[] = [
  {
    element: (
      <AuthProvider>
        <NewRelicRoute />
        <Outlet />
      </AuthProvider>
    ),
    children: [
      {
        index: true,
        id: "app_entry",
        element: <AppEntryShell />,
      },
      {
        path: APP_PATHS.SUPPORT_ADMIN,
        element: <SuperAdminShell />,
        errorElement: <ErrorBoundary />,
        hydrateFallbackElement: lazyRouteFallback,
        children: [
          {
            index: true,
            id: "support_admin",
            lazy: {
              Component: () =>
                lazyDefault(() => import("@/pages/SuperAdmin/SuperAdminHome")),
            },
          },
        ],
      },
      {
        element: <ProtectedRoot />,
        errorElement: <ErrorBoundary />,
        path: `:${ROUTE_PARAMS.ACCOUNT_ID}?`,
        hydrateFallbackElement: lazyRouteFallback,
        children: [
          {
            id: "access_denied",
            path: ROUTE_PARAMS.ACCESS_DENIED,
            lazy: {
              Component: () =>
                lazyDefault(() => import("@/pages/AccessDenied")),
            },
          },
          {
            id: "file_not_found",
            path: ROUTE_PARAMS.FILE_NOT_FOUND,
            lazy: {
              Component: () =>
                lazyDefault(() => import("@/pages/FileNotFound")),
            },
          },
          {
            element: (
              <DenyDealerWithoutRole fallback={<NavigateToAccessDenied />}>
                <Outlet />
              </DenyDealerWithoutRole>
            ),
            children: [
              {
                id: "dashboard",
                index: true,
                lazy: {
                  Component: () =>
                    lazyDefault(() => import("@/pages/Dashboard/Dashboard")),
                },
              },
              {
                path: "customers",
                element: permissionOutlet(
                  PermissionCategory.Customer,
                  PermissionAction.View
                ),
                children: [
                  {
                    id: "customers",
                    index: true,
                    lazy: {
                      Component: () =>
                        lazyDefault(() => import("@/pages/Customers")),
                    },
                  },
                  {
                    path: `create`,
                    element: permissionOutlet(
                      PermissionCategory.Customer,
                      PermissionAction.Create
                    ),
                    children: [
                      {
                        id: "create_customer",
                        index: true,
                        lazy: {
                          Component: () =>
                            lazyDefault(
                              () => import("@/pages/Customers/CreateCustomer")
                            ),
                        },
                      },
                    ],
                  },
                  {
                    path: `:${ROUTE_PARAMS.CUSTOMER_ID}`,
                    element: (
                      <CustomerRouteProvider>
                        <Outlet />
                      </CustomerRouteProvider>
                    ),
                    children: [
                      {
                        id: "customer",
                        index: true,
                        lazy: {
                          Component: () =>
                            lazyDefault(
                              () => import("@/pages/Customers/Customer")
                            ),
                        },
                      },
                      {
                        path: ROUTE_PARAMS.BULK_SHIP_TO_CONTACT,
                        children: [
                          {
                            element: permissionOutlet(
                              PermissionCategory.Customer,
                              PermissionAction.Manage
                            ),
                            children: [
                              {
                                id: "bulk_ship_to_contact",
                                index: true,
                                lazy: {
                                  Component: loadChangeShipToContactRoute,
                                },
                              },
                            ],
                          },
                          {
                            path: `:${ROUTE_PARAMS.BULK_OPERATION_ID}`,
                            element: permissionOutlet(
                              PermissionCategory.Customer,
                              PermissionAction.View
                            ),
                            children: [
                              {
                                id: "bulk_ship_to_contact_view",
                                index: true,
                                lazy: {
                                  Component: loadChangeShipToContactRoute,
                                },
                              },
                            ],
                          },
                        ],
                      },
                      {
                        path: `:${ROUTE_PARAMS.CONTRACT_ID}`,
                        children: [
                          {
                            id: "customer_contract",
                            index: true,
                            lazy: {
                              Component: () =>
                                lazyDefault(
                                  () => import("@/pages/Contracts/Contract")
                                ),
                            },
                          },
                          {
                            path: "amend",
                            element: permissionOutlet(
                              PermissionCategory.Contract,
                              PermissionAction.CreateAndManage
                            ),
                            children: [
                              {
                                id: "customer_amend_contract",
                                index: true,
                                lazy: {
                                  Component: () =>
                                    lazyDefault(
                                      () =>
                                        import("@/pages/Contracts/Contract/Amend2")
                                    ),
                                },
                              },
                            ],
                          },
                          {
                            path: `amendments/:${ROUTE_PARAMS.QUOTE_ID}`,
                            children: [
                              {
                                id: "customer_amendment_summary",
                                index: true,
                                lazy: {
                                  Component: () =>
                                    lazyDefault(
                                      () =>
                                        import("@/pages/Contracts/Contract/Amendment")
                                    ),
                                },
                              },
                            ],
                          },
                          {
                            path: `dcountry-form/:${ROUTE_PARAMS.QUOTE_ID}`,
                            children: [
                              {
                                id: "customer_dcountry_form",
                                index: true,
                                lazy: {
                                  Component: () =>
                                    lazyDefault(
                                      () =>
                                        import("@/components/Forms/DCountry")
                                    ),
                                },
                              },
                            ],
                          },
                        ],
                      },
                      {
                        path: `create`,
                        element: permissionOutlet(
                          PermissionCategory.Contract,
                          PermissionAction.CreateAndManage
                        ),
                        children: [
                          {
                            id: "create_customer_contract",
                            index: true,
                            lazy: {
                              Component: () =>
                                lazyDefault(
                                  () =>
                                    import("@/pages/Contracts/CreateContract")
                                ),
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                id: "dealer",
                path: "dealer",
                children: [
                  {
                    id: "dealer_index",
                    index: true,
                    element: (
                      <FeatureFlaggedComponent
                        flagName={UiFeatureFlagNames.DealerPage}
                        fallback={<Route404 />}
                      >
                        <PageTitle
                          title={i18n.t("subnav_items.title.dealer")}
                        />
                      </FeatureFlaggedComponent>
                    ),
                  },
                ],
              },
              {
                path: "invoices",
                element: permissionOutlet(
                  PermissionCategory.Invoice,
                  PermissionAction.View
                ),
                children: [
                  {
                    id: "invoices",
                    index: true,
                    lazy: {
                      Component: () =>
                        lazyDefault(() => import("@/pages/Invoices")),
                    },
                  },
                ],
              },
              {
                path: "devices",
                element: permissionOutlet(
                  PermissionCategory.Device,
                  PermissionAction.View
                ),
                children: [
                  {
                    id: "devices",
                    index: true,
                    lazy: {
                      Component: () =>
                        lazyDefault(() => import("@/pages/Devices")),
                    },
                  },
                ],
              },
              {
                path: "settings",
                children: [
                  {
                    id: "settings",
                    index: true,
                    lazy: {
                      Component: () =>
                        lazyDefault(() => import("@/pages/Settings/index")),
                    },
                  },
                  {
                    path: ROUTE_PARAMS.BULK_EMAIL_NOTICATIONS,
                    element: permissionOutlet(
                      PermissionCategory.EmailSettings,
                      PermissionAction.ManageSettings
                    ),
                    children: [
                      {
                        id: "bulk_email_notifications",
                        index: true,
                        lazy: {
                          Component: () =>
                            lazyDefault(
                              () =>
                                import("@/pages/Settings/NotificationSettings/components/BulkEmailNotificationSettings")
                            ),
                        },
                      },
                    ],
                  },
                ],
              },
              {
                path: "dealer-members",
                element: permissionOutlet(
                  PermissionCategory.Dealer,
                  PermissionAction.View
                ),
                children: [
                  {
                    id: "dealer-members",
                    index: true,
                    lazy: {
                      Component: () =>
                        lazyDefault(() => import("@/pages/DealerMembers")),
                    },
                  },
                ],
              },
              {
                path: "audit-logs",
                element: permissionOutlet(
                  PermissionCategory.Audit,
                  PermissionAction.View
                ),
                children: [
                  {
                    id: "audit-logs",
                    index: true,
                    lazy: {
                      Component: () =>
                        lazyDefault(
                          () => import("@/pages/AuditLogs/AuditLogPage")
                        ),
                    },
                  },
                ],
              },
              {
                path: "product-pricelist/download/:id?",
                id: "product-pricelist-download",
                element: (
                  <PermissionGate
                    resource={PermissionCategory.Product}
                    action={PermissionAction.Export}
                    fallback={<NavigateToAccessDenied />}
                  >
                    <FeatureFlaggedComponent
                      flagName={UiFeatureFlagNames.ExportPriceList}
                      fallback={<Route404 />}
                    >
                      <Outlet />
                    </FeatureFlaggedComponent>
                  </PermissionGate>
                ),
                children: [
                  {
                    index: true,
                    lazy: {
                      Component: () =>
                        lazyDefault(
                          () =>
                            import("@/pages/ProductPricelist/ProductPricelistDownloadRedirect")
                        ),
                    },
                  },
                ],
              },
              {
                path: "product-pricelist",
                element: (
                  <DealerOwnerRoleGate>
                    <FeatureFlaggedComponent
                      flagName={UiFeatureFlagNames.ExportPriceList}
                      fallback={<Route404 />}
                    >
                      <Outlet />
                    </FeatureFlaggedComponent>
                  </DealerOwnerRoleGate>
                ),
                children: [
                  {
                    id: "product-pricelist",
                    index: true,
                    lazy: {
                      Component: () =>
                        lazyDefault(() => import("@/pages/ProductPricelist")),
                    },
                  },
                ],
              },
              {
                path: "contracts",
                element: permissionOutlet(
                  PermissionCategory.Contract,
                  PermissionAction.View
                ),
                children: [
                  {
                    id: "contracts",
                    index: true,
                    lazy: {
                      Component: () =>
                        lazyDefault(() => import("@/pages/Contracts")),
                    },
                  },
                  {
                    path: `:${ROUTE_PARAMS.CONTRACT_ID}`,
                    children: [
                      {
                        id: "contract",
                        index: true,
                        lazy: {
                          Component: () =>
                            lazyDefault(
                              () => import("@/pages/Contracts/Contract")
                            ),
                        },
                      },
                      {
                        path: "amend",
                        element: permissionOutlet(
                          PermissionCategory.Contract,
                          PermissionAction.CreateAndManage
                        ),
                        children: [
                          {
                            id: "amend_contract",
                            index: true,
                            lazy: {
                              Component: () =>
                                lazyDefault(
                                  () =>
                                    import("@/pages/Contracts/Contract/Amend2")
                                ),
                            },
                          },
                        ],
                      },
                      {
                        path: `amendments/:${ROUTE_PARAMS.QUOTE_ID}`,
                        children: [
                          {
                            id: "amendment_summary",
                            index: true,
                            lazy: {
                              Component: () =>
                                lazyDefault(
                                  () =>
                                    import("@/pages/Contracts/Contract/Amendment")
                                ),
                            },
                          },
                        ],
                      },
                      {
                        path: `dcountry-form/:${ROUTE_PARAMS.QUOTE_ID}`,
                        children: [
                          {
                            id: "dcountry_form",
                            index: true,
                            lazy: {
                              Component: () =>
                                lazyDefault(
                                  () => import("@/components/Forms/DCountry")
                                ),
                            },
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "create",
                    element: permissionOutlet(
                      PermissionCategory.Contract,
                      PermissionAction.CreateAndManage
                    ),
                    children: [
                      {
                        id: "create_contract",
                        index: true,
                        lazy: {
                          Component: () =>
                            lazyDefault(
                              () => import("@/pages/Contracts/CreateContract")
                            ),
                        },
                      },
                    ],
                  },
                  {
                    id: "contract_version",
                    path: `:${ROUTE_PARAMS.CONTRACT_ID}/versions/:versionId`,
                    lazy: {
                      Component: async () => {
                        const ContractVersion = await lazyDefault(
                          () =>
                            import("@/pages/ContractVersion/ContractVersion")
                        );

                        return function ContractVersionFeatureFlagged() {
                          return (
                            <FeatureFlaggedComponent
                              flagName={UiFeatureFlagNames.ContractVersioning}
                              fallback={<Route404 />}
                            >
                              <ContractVersion />
                            </FeatureFlaggedComponent>
                          );
                        };
                      },
                    },
                  },
                ],
              },
              {
                path: "*",
                id: "not_found",
                element: <Route404 />,
              },
            ],
          },
        ],
      },
      {
        path: `:${ROUTE_PARAMS.ACCOUNT_ID}/invoice/:id/download`,
        element: (
          <ProtectedRoute>
            <FileDownloadRedirect resourceType="invoice" />
          </ProtectedRoute>
        ),
      },
      {
        path: `:${ROUTE_PARAMS.ACCOUNT_ID}/credit-memo/:id/download`,
        element: (
          <ProtectedRoute>
            <FileDownloadRedirect resourceType="credit-memo" />
          </ProtectedRoute>
        ),
      },
      {
        path: "callback",
        id: "callback",
        // Serial PKCE token exchange; see AuthCallback (library callback is skipped).
        element: <AuthCallback />,
      },
      {
        path: "logout",
        id: "logout",
        element: <FullScreenLoading />,
      },
    ],
  },
];

export default routes;
